import React, {useState, useEffect, useCallback} from 'react';
import {connect} from 'react-redux';
import {Link, Redirect, useLocation} from 'react-router-dom';
import {Dropdown, Sidebar} from '../shared';
import {userInitials, isAdmin} from '../../helpers';
import {logout, refreshUser} from '../../auth';
import {getActionCounts, getListingsPendingCount} from '../../api/adminApi';
import history from '../../history';
import logo from '../../images/logo6.png';

const defaultActionCounts = { group_trips: 0, shared_rentals: 0, private_tours: 0 };

/** Drop items with show: false; prune empty nested groups. */
function filterSidebarMenu(items) {
	return items
		.filter((item) => item.show !== false)
		.map((item) => {
			if (!item.children) return item;
			const children = filterSidebarMenu(item.children);
			return children.length ? { ...item, children } : null;
		})
		.filter(Boolean);
}

const AdminLayout = ({currentUser, children}) => {
	const loggedIn = currentUser && currentUser.email !== undefined;
	const location = useLocation();
	const [section, setSection] = useState('');
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const [actionCounts, setActionCounts] = useState(defaultActionCounts);
	const [listingsPendingTotal, setListingsPendingTotal] = useState(0);

	// Only Admin and Support can access admin; everyone else goes home
	if (loggedIn && !isAdmin(currentUser)) {
		return <Redirect to="/" />;
	}

	// Fetch latest user when entering admin so UI reflects server state (email/role changes)
	useEffect(() => {
		if (loggedIn) refreshUser();
	}, []);

	const fetchActionCounts = useCallback(() => {
		if (!loggedIn || !isAdmin(currentUser)) return;
		getActionCounts()
			.then((res) => {
				const d = res.data || {};
				setActionCounts({
					group_trips: Number(d.group_trips) || 0,
					shared_rentals: Number(d.shared_rentals) || 0,
					private_tours: Number(d.private_tours) || 0
				});
			})
			.catch(() => {});
	}, [loggedIn, currentUser]);

	const fetchListingsPending = useCallback(() => {
		if (!loggedIn || !isAdmin(currentUser)) return;
		getListingsPendingCount()
			.then((res) => setListingsPendingTotal(Number(res.data?.total) || 0))
			.catch(() => {});
	}, [loggedIn, currentUser]);

	// Fetch action counts on mount and every 60s for sidebar badges (admin only)
	useEffect(() => {
		fetchActionCounts();
		fetchListingsPending();
		const interval = setInterval(() => { fetchActionCounts(); fetchListingsPending(); }, 60000);
		return () => clearInterval(interval);
	}, [fetchActionCounts, fetchListingsPending]);

	// Refresh sidebar pending count when an admin approves/rejects a listing (e.g. on vendor detail)
	useEffect(() => {
		const handler = () => fetchListingsPending();
		window.addEventListener('admin:refresh-pending-count', handler);
		return () => window.removeEventListener('admin:refresh-pending-count', handler);
	}, [fetchListingsPending]);

	// Browser tab title: app name first so tab text is visible and recognizable when truncated
	useEffect(() => {
		const p = location.pathname || '';
		if (p === '/admin/summaries') document.title = 'Visit All Nepal – Vendor payouts';
		else if (p === '/admin/transactions' || p.startsWith('/admin/transaction/')) document.title = 'Visit All Nepal – Customer payments';
		else if (p === '/admin/service_transactions' || p.startsWith('/admin/service_transaction')) document.title = 'Visit All Nepal – Vendor ledger';
		else if (p === '/admin/partners' || (p.startsWith('/admin/partner') && p !== '/admin/partners')) document.title = 'Visit All Nepal – All Vendors';
		else if (p === '/admin/users_list' || p.startsWith('/admin/user')) document.title = 'Visit All Nepal – Users';
		else if (p.startsWith('/admin')) document.title = 'Visit All Nepal – Admin';
	}, [location.pathname]);

	useEffect(() => {
		const p = location.pathname || '';
		const hash = (location.hash || (typeof window !== 'undefined' && window.location && window.location.hash) || '').replace(/^#/, '');
		if (p === '/admin/partners' || p.startsWith('/admin/partner')) setSection('vendors_partners');
		else if (p === '/admin/summaries') setSection('vendors_payouts');
		else if (p === '/admin/manage_group_trips') setSection('manage_group_trips');
		else if (p.startsWith('/admin/group_tour')) setSection('group_tours');
		else if (p === '/admin/manage_shared_rentals') setSection('manage_shared_rentals');
		else if (p.startsWith('/admin/shared_rental')) setSection('shared_rentals');
		else if (p === '/admin/booking_calendar') setSection('booking_calendar');
		else if (p.startsWith('/admin/booking_catalog')) setSection('booking_catalog');
		else if (p === '/admin/manage_private_tours') setSection('manage_private_tours');
		else if (p === '/admin/private_tours' || p.startsWith('/admin/private_tour_')) setSection('private_tours');
		else if (p === '/admin/packages' || p.startsWith('/admin/package_')) setSection('packages');
		else if (p === '/admin/transactions' || p.startsWith('/admin/transaction/')) setSection('payments_customer');
		else if (p === '/admin/service_transactions' || p.startsWith('/admin/service_transaction')) setSection('payments_vendor');
		else if (p === '/admin/settings') setSection('settings');
		else if (p === '/admin/users_list' || p.startsWith('/admin/user')) setSection('users');
		else if (p.startsWith('/admin/cms')) setSection('cms');
		else if (p === '/admin' || p === '/admin/') {
			if (hash && ['packages', 'users', 'shared_rentals', 'vendors'].includes(hash)) setSection(hash);
			else setSection('');
		}
	}, [location.pathname, location.hash]);

	const rawSideBarMenu = [
		{
			icon: 'fas fa-columns',
			name: '',
			label: 'My Dashboard',
			value: '',
			active: section === '',
			show: false,
		},
		{
			name: 'cms',
			label: 'Content',
			icon: 'fas fa-globe-asia',
			active: section === 'cms',
		},
		{
			name: 'vendors',
			label: 'Vendors',
			children: [
				{
					label: 'Vendors',
					children: [
						{ name: 'vendors_partners', label: 'All Vendors', icon: 'fas fa-user-tie', active: section === 'vendors_partners', value: listingsPendingTotal > 0 ? String(listingsPendingTotal) : '', badgeVariant: listingsPendingTotal > 0 ? 'danger' : null, title: listingsPendingTotal > 0 ? `${listingsPendingTotal} listing${listingsPendingTotal !== 1 ? 's' : ''} pending approval` : '' },
						{ name: 'vendors_payouts', label: 'Vendor Payouts', icon: 'fas fa-wallet', active: section === 'vendors_payouts' }
					]
				}
			]
		},
		{
			name: 'manage_group',
			label: 'Manage',
			children: [
				{
					label: 'Overview',
					children: [
						{
							name: 'booking_calendar',
							label: 'Booking Calendar',
							icon: 'fas fa-calendar-alt',
							active: section === 'booking_calendar',
						},
						{
							name: 'booking_catalog',
							label: 'Booking Catalog',
							icon: 'fas fa-list-alt',
							active: section === 'booking_catalog',
						},
						{
							name: 'users',
							label: 'Users',
							details: 'Accounts, roles & vendor conversion',
							icon: 'fas fa-users',
							active: section === 'users',
							link: '/admin/users_list',
						},
					],
				},
				{
					label: 'Group Trips',
					children: [
						{ name: 'group_tours', label: 'Group Tours', icon: 'fas fa-list', active: section === 'group_tours', show: false },
						{
							name: 'manage_group_trips',
							label: 'Manage Group Trips',
							icon: 'fas fa-users-cog',
							active: section === 'manage_group_trips',
							value: actionCounts.group_trips > 0 ? String(actionCounts.group_trips) : '',
							badgeVariant: actionCounts.group_trips > 0 ? 'danger' : null,
							title: actionCounts.group_trips > 0 ? `${actionCounts.group_trips} trip${actionCounts.group_trips !== 1 ? 's' : ''} need your action` : '',
							show: false,
						}
					]
				},
				{
					label: 'Shared Rentals',
					children: [
						{ name: 'shared_rentals', label: 'Shared Rentals', icon: 'fas fa-list', active: section === 'shared_rentals', show: false },
						{
							name: 'manage_shared_rentals',
							label: 'Manage Shared Rentals',
							icon: 'fas fa-car-side',
							active: section === 'manage_shared_rentals',
							value: actionCounts.shared_rentals > 0 ? String(actionCounts.shared_rentals) : '',
							badgeVariant: actionCounts.shared_rentals > 0 ? 'danger' : null,
							title: actionCounts.shared_rentals > 0 ? `${actionCounts.shared_rentals} rental${actionCounts.shared_rentals !== 1 ? 's' : ''} need your action` : '',
							show: false,
						}
					]
				},
				{
					label: 'Private Tours',
					children: [
						{ name: 'private_tours', label: 'Private Tours', icon: 'fas fa-briefcase', active: section === 'private_tours', show: false },
						{
							name: 'manage_private_tours',
							label: 'Manage Private Tours',
							icon: 'fas fa-mountain',
							active: section === 'manage_private_tours',
							value: actionCounts.private_tours > 0 ? String(actionCounts.private_tours) : '',
							badgeVariant: actionCounts.private_tours > 0 ? 'danger' : null,
							title: actionCounts.private_tours > 0 ? `${actionCounts.private_tours} inquiry${actionCounts.private_tours !== 1 ? 'ies' : ''} need your action` : '',
							show: false,
						}
					]
				}
			]
		},
		{
			name: 'payments',
			label: 'Payments',
			children: [
				{
					label: 'Payments',
					children: [
						{
							name: 'payments_customer',
							label: 'Customer payments',
							details: 'Money received from bookings',
							icon: 'fas fa-hand-holding-usd',
							active: section === 'payments_customer',
						},
						{
							name: 'payments_vendor',
							label: 'Vendor ledger',
							details: 'Credits owed & payouts sent',
							icon: 'fas fa-file-invoice-dollar',
							active: section === 'payments_vendor',
						},
					],
				},
			],
		},
		{
			icon: 'fas fa-archive',
			name: 'others',
			label: 'Others',
			value: '',
			active: section == 'others',
			show: false,
		},
		{
			icon: 'fas fa-cog',
			name: 'settings',
			label: 'Settings',
			value: '',
			active: section === 'settings'
		}
	];

	const sideBarMenu = filterSidebarMenu(rawSideBarMenu);

	const handleNav = (data) => {
		setSection(data);
		setMobileMenuOpen(false);
		if (data === 'vendors_partners') history.push('/admin/partners');
		else if (data === 'cms') history.push('/admin/cms');
		else if (data === 'vendors_payouts') history.push('/admin/summaries');
		else if (data === 'group_tours') history.push('/admin/group_tours');
		else if (data === 'booking_calendar') history.push('/admin/booking_calendar');
		else if (data === 'booking_catalog') history.push('/admin/booking_catalog');
		else if (data === 'manage_group_trips') history.push('/admin/manage_group_trips');
		else if (data === 'shared_rentals') history.push('/admin/shared_rentals');
		else if (data === 'manage_shared_rentals') history.push('/admin/manage_shared_rentals');
		else if (data === 'manage_private_tours') history.push('/admin/manage_private_tours');
		else if (data === 'private_tours') history.push('/admin/private_tours');
		else if (data === 'packages') history.push('/admin/packages');
		else if (data === 'settings') history.push('/admin/settings');
		else if (data === 'payments_customer') history.push('/admin/transactions');
		else if (data === 'payments_vendor') history.push('/admin/service_transactions');
		else if (data === 'users') history.push('/admin/users_list');
		else history.push(`/admin#${data}`);
	};

	return (
		<div id='content'>
			{/* Mobile menu backdrop */}
			{mobileMenuOpen && (
				<div
					className='admin-mobile-backdrop d-md-none'
					onClick={() => setMobileMenuOpen(false)}
					aria-hidden="true"
				/>
			)}
			{/* Mobile sidebar drawer */}
			<div className={`admin-mobile-drawer d-md-none ${mobileMenuOpen ? 'open' : ''}`}>
				<div className='admin-mobile-drawer-header'>
					<span className='admin-mobile-drawer-title'>Admin menu</span>
					<button
						type='button'
						className='admin-mobile-drawer-close btn btn-link text-white p-2'
						onClick={() => setMobileMenuOpen(false)}
						aria-label='Close menu'
					>
						<i className='fas fa-times' />
					</button>
				</div>
				<div className='admin-mobile-drawer-body'>
					<Sidebar items={sideBarMenu} onItemSelect={handleNav} />
				</div>
			</div>
			<nav className='navbar navbar-expand-lg navbar-dark bg-primary sticky-top'>
				<div className='container align-items-stretch'>
					<div className='navbar-header d-flex align-items-center flex-grow-1'>
						<button
							type='button'
							className='admin-mobile-menu-btn d-flex d-md-none align-items-center justify-content-center btn btn-link text-white p-2 mr-2'
							onClick={() => setMobileMenuOpen(true)}
							aria-label='Open menu'
						>
							<i className='fas fa-bars' style={{ fontSize: '1.25rem' }} />
						</button>
						<Link to='/' className='navbar-brand animated bounce delay-2s'>
							<img src={logo} style={{width: "140px"}} alt='Visit All Nepal' />
						</Link>
						{loggedIn && (
							<span className='badge badge-light align-self-center ml-2' title='You have Admin access'>
								Admin
							</span>
						)}
					</div>

					<div className='navbar-collapse collapse align-items-stretch'>
						<span className='navbar-nav ml-auto align-items-stretch' />
					</div>
					<div className='d-flex align-items-stretch'>
						{loggedIn && (
							<Dropdown icon='icon-user' title={userInitials(currentUser)} className='text-white pl-3'>
								<ul className='text-normal'>
									<li className='m-0'>
										<a className='item text-bold' onClick={logout}>
											Logout
										</a>
									</li>
								</ul>
							</Dropdown>
						)}

						{!loggedIn && (
							<div className='d-flex align-items-center login'>
								<Link to='/login' className='text-bold text-white pl-3'>
									LOGIN
								</Link>
							</div>
						)}
					</div>
				</div>
			</nav>
			<div className='row'>
				<div className='d-none d-md-block col-md-2 p-0 admin-sidebar-col'>
					<Sidebar items={sideBarMenu} onItemSelect={handleNav} />
				</div>
				<div className='col-12 col-md-10 p-4 admin-main-content'>
					{children}
				</div>
			</div>
		</div>
	);
};

const mapStateToProps = ({userStore}) => ({
	currentUser: userStore.currentUser
});

export default connect(mapStateToProps)(AdminLayout);
